/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import React from "react";
import { Box, CardContent, Skeleton, Typography } from "@mui/material";
import T from "i18n-react";
import { currencyAmountFromCents } from "../../../utils/money";
import PropTypes from "prop-types";

const OrderSummary = ({ amount, dueDate, toName, fromName }) => {
  const getPrice = () => {
    if (amount != null) return currencyAmountFromCents(amount);
    return <Skeleton variant="rectangular" width={110} height={40} />;
  };

  return (
    <CardContent sx={{ p: 4 }}>
      <Box>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {getPrice()}
          </Typography>
          {dueDate && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 3
              }}
            >
              {T.translate("general.due")} {dueDate}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 4 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", minWidth: 48 }}
          >
            {T.translate("general.to")}
          </Typography>
          <Typography variant="body2">{toName}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 4 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", minWidth: 48 }}
          >
            {T.translate("general.from")}
          </Typography>
          <Typography variant="body2">{fromName}</Typography>
        </Box>
      </Box>
    </CardContent>
  );
};

OrderSummary.propTypes = {
  amount: PropTypes.number.isRequired,
  dueDate: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  fromName: PropTypes.string.isRequired
};

export default OrderSummary;
